import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, CheckCircle, XCircle, Wifi, WifiOff, RefreshCw, Volume2, VolumeX, AlertTriangle } from 'lucide-react';

const POLL_INTERVAL_MS = 4000;
const AUTO_PRINT_KEY = 'foodflows_print_station_autoprint';

export default function PrintStationPage() {
  const { apiKey } = useParams();
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const [status, setStatus] = useState('connecting');
  const [restaurantName, setRestaurantName] = useState('');
  const [printed, setPrinted] = useState([]);
  const [lastPoll, setLastPoll] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  // Auto-print: persisted across reloads via localStorage so the user only has
  // to activate it once per device.
  const [autoStarted, setAutoStarted] = useState(() => {
    try { return localStorage.getItem(AUTO_PRINT_KEY) === '1'; } catch { return false; }
  });
  const [pendingQueue, setPendingQueue] = useState([]);
  const [testing, setTesting] = useState(false);

  const iframeRef = useRef(null);
  const printingRef = useRef(false);
  const processedRef = useRef(new Set());
  const autoStartedRef = useRef(autoStarted);

  useEffect(() => {
    autoStartedRef.current = autoStarted;
    try { localStorage.setItem(AUTO_PRINT_KEY, autoStarted ? '1' : '0'); } catch { /* ignore */ }
  }, [autoStarted]);

  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880;
      o.type = 'sine';
      g.gain.setValueAtTime(0.25, ctx.currentTime);
      o.start();
      o.stop(ctx.currentTime + 0.25);
    } catch (e) { /* ignore */ }
  }, [soundEnabled]);

  const markPrinted = useCallback(async (orderId) => {
    try {
      await fetch(`${baseUrl}/api/print/station/${apiKey}/printed/${orderId}`, { method: 'POST' });
    } catch (e) { /* ignore */ }
  }, [apiKey, baseUrl]);

  const recordPrinted = useCallback((job) => {
    setPrinted(prev => [{
      order_number: job.order_number,
      customer_name: job.customer_name,
      total: job.total,
      order_type: job.order_type,
      printed_at: new Date(),
    }, ...prev].slice(0, 50));
  }, []);

  // Print via hidden iframe + window.print()
  const printViaBrowser = useCallback(async (job) => {
    return new Promise((resolve) => {
      const iframe = iframeRef.current;
      if (!iframe) return resolve(false);
      const receiptUrl = `${baseUrl}/api/print/station/${apiKey}/receipt/${job.id}?t=${Date.now()}`;
      let timer;
      const onLoad = () => {
        iframe.removeEventListener('load', onLoad);
        clearTimeout(timer);
        // Small delay so content fully renders before printing
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            resolve(true);
          } catch (e) {
            console.error('[PrintStation] print() failed', e);
            resolve(false);
          }
        }, 500);
      };
      iframe.addEventListener('load', onLoad);
      // Timeout safety net
      timer = setTimeout(() => {
        iframe.removeEventListener('load', onLoad);
        resolve(false);
      }, 10000);
      iframe.src = receiptUrl;
    });
  }, [apiKey, baseUrl]);

  // Manual print of pending queue (user-gesture button click)
  const printNext = useCallback(async () => {
    if (pendingQueue.length === 0 || printingRef.current) return;
    printingRef.current = true;
    const job = pendingQueue[0];
    const ok = await printViaBrowser(job);
    if (ok) {
      await markPrinted(job.id);
      recordPrinted(job);
    }
    setPendingQueue(q => q.slice(1));
    // Small cooldown so the print dialog or driver can settle
    setTimeout(() => { printingRef.current = false; }, 1500);
  }, [pendingQueue, printViaBrowser, markPrinted, recordPrinted]);

  const handleTestPrint = useCallback(async () => {
    setTesting(true);
    try {
      const res = await fetch(`${baseUrl}/api/print/station/${apiKey}`);
      if (!res.ok) {
        setErrorMsg('Kon Print Station niet bereiken');
        return;
      }
      const data = await res.json();
      const job = data.jobs?.[0];
      if (!job) {
        setErrorMsg('Geen bestellingen in wachtrij. Plaats eerst een test-bestelling.');
        return;
      }
      await printViaBrowser(job);
    } finally {
      setTesting(false);
    }
  }, [apiKey, baseUrl, printViaBrowser]);

  const poll = useCallback(async () => {
    // Don't poll while a print is in progress (prevents races)
    if (printingRef.current) return;
    try {
      const res = await fetch(`${baseUrl}/api/print/station/${apiKey}`);
      if (!res.ok) {
        setStatus('error');
        setErrorMsg(res.status === 404 ? 'Ongeldige API key' : `Fout ${res.status}`);
        return;
      }
      const data = await res.json();
      setStatus('connected');
      setErrorMsg('');
      setRestaurantName(data.restaurant_name || '');
      setLastPoll(new Date());

      const newJobs = (data.jobs || []).filter(j => !processedRef.current.has(j.id));
      if (newJobs.length === 0) return;
      newJobs.forEach(j => processedRef.current.add(j.id));
      playBeep();

      if (autoStartedRef.current) {
        // AUTO-PRINT MODE — fire window.print() automatically for each new job.
        // Requires Chrome kiosk-printing or default-printer "remember" setup
        // for fully silent prints.
        printingRef.current = true;
        for (const job of newJobs) {
          const ok = await printViaBrowser(job);
          if (ok) {
            await markPrinted(job.id);
            recordPrinted(job);
          }
          await new Promise(r => setTimeout(r, 2000));
        }
        printingRef.current = false;
      } else {
        // MANUAL MODE — queue jobs and wait for user to click "Print Volgende"
        setPendingQueue(q => [...q, ...newJobs]);
      }
    } catch (e) {
      setStatus('error');
      setErrorMsg('Kan geen verbinding maken met server');
    }
  }, [apiKey, baseUrl, playBeep, printViaBrowser, markPrinted, recordPrinted]);

  useEffect(() => {
    // Defer initial poll to next microtask so React doesn't flag this effect
    // for synchronously setting state during render.
    const initial = setTimeout(poll, 0);
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [poll]);

  // Keep screen awake
  useEffect(() => {
    let wakeLock = null;
    const requestLock = async () => {
      try { if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen'); }
      catch { /* ignore */ }
    };
    requestLock();
    const onVis = () => { if (document.visibilityState === 'visible') requestLock(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (wakeLock) wakeLock.release().catch(() => {});
    };
  }, []);

  const statusColor = status === 'connected' ? 'emerald' : status === 'error' ? 'red' : 'amber';
  const StatusIcon = status === 'connected' ? Wifi : status === 'error' ? WifiOff : RefreshCw;

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="print-station-page">
      <iframe
        ref={iframeRef}
        title="print-frame"
        style={{ position: 'fixed', width: 0, height: 0, border: 0, visibility: 'hidden' }}
        data-testid="print-station-iframe"
      />

      <style>{`
        @media print {
          body > *:not(iframe) { display: none !important; }
          iframe { visibility: visible !important; position: absolute !important; left: 0; top: 0; width: 100%; height: 100%; }
        }
      `}</style>

      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center justify-center">
            <Printer className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-bold text-lg">FoodFlows Print Station</h1>
            <p className="text-slate-400 text-xs">{restaurantName || 'Verbinden...'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSoundEnabled(s => !s)}
            className="text-slate-400 hover:text-white transition p-2"
            data-testid="print-station-sound-toggle"
            title={soundEnabled ? 'Geluid uit' : 'Geluid aan'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-${statusColor}-500/20 border border-${statusColor}-500/40 text-${statusColor}-300`}>
            <StatusIcon className={`w-4 h-4 ${status === 'connecting' ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">
              {status === 'connected' ? 'Verbonden' : status === 'error' ? 'Geen verbinding' : 'Verbinden...'}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Browser auto-print setup card — only when auto-print not yet on */}
        {!autoStarted && (
          <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-800/20 border border-emerald-500/40 rounded-2xl p-5" data-testid="autoprint-setup">
            <h3 className="font-bold text-emerald-200 text-lg flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5" /> Auto-Print Setup
            </h3>
            <div className="bg-emerald-950/40 border border-emerald-700/30 rounded-lg p-3 mb-4">
              <p className="text-emerald-100 text-sm font-semibold mb-2">⚡ Voor 100% Automatisch op Desktop (Aanbevolen):</p>
              <p className="text-emerald-200/80 text-xs mb-2">
                Start Chrome in <strong>Kiosk Mode</strong> — élke print verloopt dan volledig stil zonder dialoog:
              </p>
              <code className="block bg-slate-900 text-emerald-300 text-xs p-2 rounded font-mono break-all">
                chrome --kiosk-printing --kiosk {typeof window !== 'undefined' ? window.location.href : ''}
              </code>
              <button
                onClick={() => {
                  const cmd = `chrome --kiosk-printing --kiosk ${window.location.href}`;
                  navigator.clipboard.writeText(cmd).then(() => alert('Commando gekopieerd! Plak in Terminal/CMD.'));
                }}
                className="mt-2 text-emerald-300 hover:text-emerald-200 text-xs underline"
                data-testid="copy-kiosk-cmd-btn"
              >
                📋 Kopieer commando
              </button>
            </div>
            <p className="text-emerald-100/70 text-sm mb-2">Of activeer normale auto-print (print-dialoog opent automatisch — vink &ldquo;Onthouden&rdquo; aan):</p>
            <ol className="text-emerald-100/90 text-sm space-y-1.5 list-decimal ml-5 mb-4">
              <li>Zet je thermische printer als <strong>standaardprinter</strong>.</li>
              <li>Wacht op een bestelling, klik dan <strong>&ldquo;Print Volgende&rdquo;</strong>.</li>
              <li>Vink in de dialoog <strong>&ldquo;Altijd deze printer gebruiken&rdquo;</strong> aan.</li>
              <li>Klik hieronder om Auto-Print te activeren.</li>
            </ol>
            <button
              onClick={() => setAutoStarted(true)}
              data-testid="activate-autoprint-btn"
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Activeer Auto-Print
            </button>
          </div>
        )}

        {/* Status card */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-slate-400 text-sm mb-1">Status</p>
              <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                {status === 'connected' && <><CheckCircle className="w-7 h-7 text-emerald-400" /> Klaar om te printen</>}
                {status === 'error' && <><XCircle className="w-7 h-7 text-red-400" /> Geen verbinding</>}
                {status === 'connecting' && <><RefreshCw className="w-7 h-7 text-amber-400 animate-spin" /> Verbinden...</>}
              </h2>
              {errorMsg && <p className="text-red-400 text-sm mt-2" data-testid="print-station-error">{errorMsg}</p>}
              {autoStarted && (
                <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Auto-Print actief
                  <button
                    onClick={() => setAutoStarted(false)}
                    className="ml-2 text-slate-400 hover:text-red-400 underline"
                    data-testid="deactivate-autoprint-btn"
                  >
                    deactiveren
                  </button>
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-sm mb-1">Geprint vandaag</p>
              <p className="text-4xl sm:text-5xl font-bold text-emerald-400" data-testid="print-station-count">{printed.length}</p>
            </div>
          </div>
          {lastPoll && (
            <p className="text-slate-500 text-xs mt-4">
              Laatste controle: {lastPoll.toLocaleTimeString('nl-NL')} · Elke {POLL_INTERVAL_MS / 1000}s · Key: <code className="bg-slate-900 px-1.5 py-0.5 rounded">...{apiKey?.slice(-8)}</code>
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={handleTestPrint}
              disabled={testing || status !== 'connected'}
              data-testid="test-print-btn"
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg"
            >
              {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Test Print
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded-lg"
              data-testid="reload-btn"
            >
              <RefreshCw className="w-4 h-4" /> Herlaad
            </button>
          </div>
        </div>

        {/* Pending queue (manual mode only) */}
        {pendingQueue.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-2xl p-5" data-testid="pending-queue">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-amber-200 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {pendingQueue.length} bon{pendingQueue.length > 1 ? 'nen' : ''} wacht{pendingQueue.length > 1 ? 'en' : ''} op print
              </h3>
            </div>
            <button
              onClick={printNext}
              data-testid="print-next-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-6 py-4 rounded-xl text-lg flex items-center justify-center gap-2"
            >
              <Printer className="w-6 h-6" />
              Print Volgende: #{pendingQueue[0].order_number} — {pendingQueue[0].customer_name}
            </button>
            {pendingQueue.length > 1 && (
              <p className="text-amber-200/70 text-xs mt-2 text-center">
                Nog {pendingQueue.length - 1} extra bonnen in wachtrij.
              </p>
            )}
          </div>
        )}

        {/* Recent printed */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="font-semibold">Recent Geprint</h3>
          </div>
          {printed.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-500">Nog geen bestellingen geprint.</div>
          ) : (
            <ul className="divide-y divide-slate-700" data-testid="print-station-history">
              {printed.map((p, i) => (
                <li key={i} className="px-6 py-3 flex items-center justify-between hover:bg-slate-800/50">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="font-medium">#{p.order_number} — {p.customer_name}</p>
                      <p className="text-slate-400 text-xs capitalize">{p.order_type} · {p.printed_at.toLocaleTimeString('nl-NL')}</p>
                    </div>
                  </div>
                  <p className="font-mono text-emerald-400">€{Number(p.total).toFixed(2)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
